import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoFilter } from "react-icons/io5";
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import FavoriteButton from './FavoriteButton';
import NetworkLogo from './NetworkLogo';
import {
  addFavoriteNetwork,
  removeFavoriteNetwork,
  removeFavoriteLine,
  getFavoriteNetworks
} from '../services/favorites';
import { getAllNetworksWithDetails, invalidateAllNetworksCache } from '../services/networkService';

// Style pour l'animation de défilement
const marqueeStyle = `
  .marquee-container {
    width: 100%;
    overflow: hidden;
    position: relative;
  }
  
  .marquee-content {
    display: inline-flex;
    white-space: nowrap;
    animation: marqueeAnimation 20s linear infinite;
  }
  
  .marquee-content:hover {
    animation-play-state: paused;
  }
  
  @keyframes marqueeAnimation {
    from { transform: translateX(0%); }
    to { transform: translateX(-50%); }
  }
  
  .separator {
    margin: 0 8px;
    opacity: 0.6;
  }
  
  .fade-left, .fade-right {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 15px;
    z-index: 2;
    pointer-events: none;
  }
  
  .fade-left {
    left: 0;
    background: linear-gradient(to right, white, rgba(255, 255, 255, 0));
  }
  
  .dark .fade-left {
    background: linear-gradient(to right, #1e293b, rgba(30, 41, 59, 0));
  }
  
  .fade-right {
    right: 0;
    background: linear-gradient(to left, white, rgba(255, 255, 255, 0));
  }
  
  .dark .fade-right {
    background: linear-gradient(to left, #1e293b, rgba(30, 41, 59, 0));
  }
  
  /* Styles pour les titres de région */
  .region-header {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
    background: linear-gradient(to right, rgba(7, 214, 251, 0.05), rgba(255, 102, 196, 0.05));
    border-radius: 0.5rem;
    justify-content: flex-start;
  }
  
  .region-title {
    font-size: 1rem;
    font-weight: 600;
    color: #64748b;
    margin-left: 0.5rem;
  }
  
  .dark .region-title {
    color: #94a3b8;
  }
  
  .region-icon {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }
  
  .dark .region-icon {
    background-color: #1e293b;
  }
  
  .region-icon img {
    width: 1.75rem;
    height: 1.75rem;
    object-fit: contain;
  }
  
  .region-divider {
    height: 1px;
    border: none;
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    border-radius: 0.5px;
    margin-top: 2rem;
    margin-bottom: 1rem;
    width: 100%;
    opacity: 0.1;
  }
  
  .region-filter-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    background-color: #f8fafc;
    border-radius: 0.75rem;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
  }
  
  .dark .region-filter-container {
    background-color: #1e293b;
    border-color: #334155;
  }
  
  .region-filter-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .dark .region-filter-label {
    color: #94a3b8;
  }
  
  .region-select {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border: double 1px transparent;
    background-image: linear-gradient(white, white), 
                    linear-gradient(135deg, #07d6fb, #ff66c4);
    background-origin: border-box;
    background-clip: padding-box, border-box;
    font-weight: 500;
    transition: all 0.2s;
    cursor: pointer;
  }
  
  .dark .region-select {
    background-image: linear-gradient(#1e293b, #1e293b), 
                    linear-gradient(135deg, #07d6fb, #ff66c4);
    color: white;
  }
  
  .region-select:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }
`;

// Style des cartes avec simple bordure en dégradé et skeleton loading
const simpleBorderStyle = `
  .simple-gradient-border {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    transition: transform 0.3s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: double 1px transparent;
    background-image: linear-gradient(white, white), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    background-origin: border-box;
    background-clip: content-box, border-box;
  }
  
  .dark .simple-gradient-border {
    background: #1e293b;
    background-image: linear-gradient(#1e293b, #1e293b), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  .simple-gradient-border:hover {
    transform: translateY(-4px);
  }
  
  .card-content {
    padding: 1rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .network-logo-container {
    width: 3.5rem;
    height: 3.5rem;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .network-logo-border {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: double 2px transparent;
    background-image: linear-gradient(white, white), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    background-origin: border-box;
    background-clip: content-box, border-box;
  }
  
  .dark .network-logo-border {
    background-image: linear-gradient(#1e293b, #1e293b), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
  }
  
  .network-logo {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }
  
  .network-name {
    color: #1e3a8a;
    font-family: 'Naotypo-Bold';
    font-weight: 900;
    letter-spacing: -0.5px;
  }
  
  .dark .network-name {
    color: #e2e8f0;
  }
  
  .agency-name {
    color: #64748b;
    font-size: 0.75rem;
  }
  
  .dark .agency-name {
    color: #94a3b8;
  }
  
  .network-type-badge {
    display: inline-block;
    font-size: 0.6rem;
    font-weight: 600;
    padding: 0.1rem 0.5rem;
    border-radius: 9999px;
    margin-left: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    color: white;
  }
  
  .page-title {
    font-size: 1.6rem;
    font-weight: bold;
    color: #1e3a8a;
    margin-bottom: 0.3rem;
  }
  
  .dark .page-title {
    color: #e2e8f0;
  }
  
  .text-gradient {
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-weight: 700;
    font-size: 1.6rem;
    text-shadow: 0px 0px 1px rgba(0,0,0,0.05);
  }
  
  .gradient-hr {
    height: 2px;
    border: none;
    background-image: linear-gradient(135deg, #07d6fb, #ff66c4);
    border-radius: 1px;
    margin-top: 0;
    margin-bottom: 1rem;
    width: 120px;
  }
  
  .search-container {
    max-width: 100%;
  }
  
  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border-radius: 2rem;
    background-color: #f8fafc;
    transition: all 0.2s;
    border: double 1px transparent;
    background-image: linear-gradient(white, white), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    background-origin: border-box;
    background-clip: padding-box, border-box;
  }
  
  .dark .search-input {
    background-color: #1e293b;
    background-image: linear-gradient(#1e293b, #1e293b), 
                      linear-gradient(135deg, #07d6fb, #ff66c4);
    color: white;
  }
  
  .search-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }
  
  .dark .search-input:focus {
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }
  
  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }
  
  /* Skeleton loader styles */
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
  
  .skeleton {
    background: linear-gradient(90deg, 
                #f0f0f0 25%, 
                #e0e0e0 50%, 
                #f0f0f0 75%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
  }
  
  .dark .skeleton {
    background: linear-gradient(90deg, 
                #334155 25%, 
                #475569 50%, 
                #334155 75%);
    background-size: 1000px 100%;
  }
  
  .skeleton-card {
    border-radius: 12px;
    overflow: hidden;
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .dark .skeleton-card {
    background-color: #1e293b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  .skeleton-content {
    padding: 1rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .skeleton-circle {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
  }
  
  .skeleton-title {
    height: 1.25rem;
    margin-top: 1rem;
    width: 80%;
  }
  
  .skeleton-subtitle {
    height: 0.75rem;
    margin-top: 0.5rem;
    width: 60%;
  }
`;

// Composant pour le défilement de texte
const TextMarquee = ({ text, className = "" }) => {
  // Pour les textes courts, affichage normal sans animation
  if (!text || text.length < 20) {
    return <span className={`${className} dark:text-gray-300`}>{text}</span>;
  }
  
  // Pour les textes longs, animation de défilement infini
  return (
    <div className="relative overflow-hidden w-full">
      <style>{marqueeStyle}</style>
      <div className="marquee-container">
        <div className="absolute top-0 bottom-0 left-0 w-[15px] z-[2] pointer-events-none bg-gradient-to-r from-white dark:from-[#1e293b] to-transparent"></div>
        <div className="marquee-content">
          <span className={`${className} dark:text-gray-300`}>{text}</span>
          <span className={`text-gray-500 dark:text-gray-400 separator ${className}`}>•</span>
          <span className={`${className} dark:text-gray-300`}>{text}</span>
          <span className={`text-gray-500 dark:text-gray-400 separator ${className}`}>•</span>
        </div>
        <div className="absolute top-0 bottom-0 right-0 w-[15px] z-[2] pointer-events-none bg-gradient-to-l from-white dark:from-[#1e293b] to-transparent"></div>
      </div>
    </div>
  );
};

// Composant pour les cartes de chargement avec effet de vague
const SkeletonNetworkCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-content">
      <div className="flex items-center justify-between">
        <div className="skeleton skeleton-circle"></div>
        <div className="skeleton" style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%' }}></div>
      </div>
      <div className="skeleton skeleton-title"></div>
      <div className="skeleton skeleton-subtitle"></div>
    </div>
  </div>
);

// Fonction pour obtenir l'image de la région
const RegionLogo = ({ region }) => {
  const [imageError, setImageError] = useState(false);
  
  // Mapper les noms de régions vers les fichiers d'image
  const getRegionImagePath = (regionName) => {
    const normalizedName = regionName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Enlever les accents
      .replace(/[^\w\s-]/g, "") // Enlever les caractères spéciaux
      .replace(/\s+/g, "-"); // Remplacer les espaces par des tirets
      
    return `/images/regions/${normalizedName}.svg`;
  };
  
  // Fallback emojis en cas d'erreur de chargement d'image
  const getFallbackEmoji = (regionName) => {
    const regionIcons = {
      "Île-de-France": "🗼",
      "Auvergne-Rhône-Alpes": "⛰️",
      "Provence-Alpes-Côte d'Azur": "🌊",
      "Occitanie": "🏞️",
      "Nouvelle-Aquitaine": "🍇",
      "Hauts-de-France": "🏭",
      "Grand Est": "🏰",
      "Bretagne": "⚓",
      "Normandie": "🧀",
      "Pays de la Loire": "🏰",
      "Bourgogne-Franche-Comté": "🍷",
      "Centre-Val de Loire": "🏯",
      "Corse": "🏝️",
      "Guadeloupe": "🌴",
      "Martinique": "🌋",
      "Guyane": "🌳",
      "La Réunion": "🌋",
      "Mayotte": "🐢",
      "Non classée": "🚌"
    };
    
    return regionIcons[regionName] || "🏙️";
  };
  
  // Afficher l'emoji si l'image ne peut pas être chargée
  if (imageError) {
    return <span style={{ fontSize: '1.5rem' }}>{getFallbackEmoji(region)}</span>;
  }
  
  return (
    <img 
      src={getRegionImagePath(region)} 
      alt={`Région ${region}`} 
      onError={() => setImageError(true)}
    />
  );
};

const NetworkList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [favoriteNetworks, setFavoriteNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [networkLines, setNetworkLines] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [networksByRegion, setNetworksByRegion] = useState({});
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('Tous');
  const [currentBusMessage, setCurrentBusMessage] = useState('');
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  // Fonction pour obtenir la salutation en fonction de l'heure
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 18) {
      return "Bonjour";
    } else {
      return "Bonsoir";
    }
  };

  // Fonction pour obtenir un message aléatoire sur les bus
  const getBusMessage = () => {
    const messages = [
      // Messages standards
      "prêt à prendre le bus aujourd'hui ?",
      "que diriez-vous d'un voyage en bus ?",
      "où souhaitez-vous vous rendre en bus ?",
      "quel sera votre itinéraire aujourd'hui ?",
      
      // Messages informatifs
      "saviez-vous que vous économisez 40 trajets en voiture en prenant le bus ?",
      "saviez-vous que le premier bus motorisé date de 1895 ?",
      "saviez-vous que les premiers bus parisiens s'appelaient les Omnibus ?",
      "saviez-vous qu'un bus peut remplacer 40 voitures en ville ?",
      
      // Messages d'engagement
      "votre trajet en bus contribue à préserver l'environnement",
      "en choisissant le bus, vous participez à la mobilité durable",
      "grâce au bus, vous aidez à réduire les embouteillages en ville",
      "votre choix du bus aide à construire une ville plus verte",
      
      // Messages accueillants
      "nous sommes ravis de vous accompagner dans vos déplacements",
      "laissez-nous vous guider vers votre destination",
      "permettez-nous de faciliter votre trajet aujourd'hui",
      "nous vous souhaitons un agréable voyage en bus"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  useEffect(() => {
    // Définir le message aléatoire au chargement initial
    setCurrentBusMessage(getBusMessage());
  }, []); // Le tableau de dépendances vide signifie que cela ne s'exécute qu'une fois au montage

  useEffect(() => {
    // Vérifier si nous avons déjà des données en cache et si elles sont récentes
    const loadData = async () => {
      const cachedData = sessionStorage.getItem('networksData');
      const cachedTimestamp = sessionStorage.getItem('networksTimestamp');
      const cachedFavorites = user ? sessionStorage.getItem(`favoriteNetworks_${user.id}`) : null;
      
      const now = Date.now();
      const isDataRecent = cachedTimestamp && (now - parseInt(cachedTimestamp)) < 5 * 60 * 1000; // 5 minutes
      
      // Si nous revenons sur la page et que les données sont en cache et récentes,
      // utiliser le cache au lieu de refaire une requête
      if (!isInitialLoad && cachedData && isDataRecent) {
        setNetworks(JSON.parse(cachedData));
        setLoading(false);
        
        if (user && cachedFavorites) {
          setFavoriteNetworks(JSON.parse(cachedFavorites));
        }
        return;
      }
      
      // Premier chargement ou données expirées, il faut les recharger
      setLoading(true);
      
      try {
        await Promise.all([
          fetchNetworks(),
          user ? fetchFavoriteNetworks() : Promise.resolve()
        ]);
      } catch (err) {
        console.error("Erreur lors du chargement des données:", err);
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };
    
    loadData();
  }, [user]);

  const fetchFavoriteNetworks = async () => {
    if (!user) {
      setFavoriteNetworks([]);
      setLoadingFavorites(false); // S'assurer que le chargement est arrêté
      return;
    }
    setLoadingFavorites(true);
    try {
      // Utilise la fonction de service getFavoriteNetworks qui est déjà mise en cache
      // et retourne toutes les données nécessaires (network, agency, region, etc.)
      const favoriteNetworksData = await getFavoriteNetworks(); 
      setFavoriteNetworks(favoriteNetworksData || []);
      
      // Le cache sessionStorage n'est plus nécessaire ici car géré par le service getFavoriteNetworks
      // if (user && favoriteNetworksData) { // Vérifier favoriteNetworksData avant stringify
      //   sessionStorage.setItem(`favoriteNetworks_${user.id}`, JSON.stringify(favoriteNetworksData));
      // }

    } catch (err) {
      console.error("Erreur lors de la récupération des réseaux favoris (NetworkList component):", err);
      //setError(err.message); // Vous pouvez choisir de gérer l'erreur différemment pour les favoris
      setFavoriteNetworks([]); // Réinitialiser en cas d'erreur
    } finally {
      setLoadingFavorites(false);
    }
  };

  const fetchNetworks = async () => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer les IDs des réseaux favoris actuels pour les passer au service
      const currentFavoriteIds = favoriteNetworks.map(fav => fav.network_id);
      const networksWithDetails = await getAllNetworksWithDetails(currentFavoriteIds);

      // La fonction de service s'occupe du cache et de la récupération des données
      // Mettre à jour les états locaux avec les données récupérées
      const networkGroups = networksWithDetails.reduce((acc, network) => {
        const region = network.region || 'Autres'; // Utiliser 'Autres' si la région n'est pas définie
        if (!acc[region]) acc[region] = [];
        acc[region].push(network);
        return acc;
      }, {});
      
      const regionList = Object.keys(networkGroups).sort((a, b) => {
        if (a === 'Autres') return 1;
        if (b === 'Autres') return -1;
        return a.localeCompare(b);
      });

      setRegions(regionList);
      setNetworksByRegion(networkGroups);
      setNetworks(networksWithDetails || []); // S'assurer que c'est un tableau
      
      // Suppression de la logique de cache sessionStorage
      // sessionStorage.setItem('networksData', JSON.stringify(networksWithDetails));
      // sessionStorage.setItem('networksTimestamp', Date.now().toString());

    } catch (err) {
      console.error("Erreur lors de la récupération des réseaux (NetworkList component fetchNetworks):", err);
      setError(err.message);
      // Réinitialiser les états en cas d'erreur pour éviter un affichage incorrect
      setNetworks([]);
      setNetworksByRegion({});
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkClick = (networkId) => {
    navigate(`/network/${networkId}/lines`);
  };

  const handleFavoriteClick = async (e, network, isFavoriteSection = false) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const isCurrentlyFavorite = favoriteNetworks.some(fav => fav.network_id === network.network_id);

      if (isCurrentlyFavorite) {
        // Vérifier si le réseau a des lignes en favoris
        const { data: favLines, error: favLinesError } = await supabase
          .from('favorite_lines')
          .select('*')
          .eq('user_id', user.id)
          .eq('network_id', network.network_id);
          
        if (favLinesError) throw favLinesError;
        
        // Si le réseau a des lignes en favoris, demander confirmation
        if (favLines && favLines.length > 0) {
          setSelectedNetwork(network);
          setNetworkLines(favLines);
          setShowConfirmDialog(true);
          return; // Sortir de la fonction pour attendre la confirmation
        }
        
        // Si pas de lignes en favoris, supprimer directement
        await removeFavoriteNetwork(network.network_id);
        setFavoriteNetworks(prevFavorites => prevFavorites.filter(fav => fav.network_id !== network.network_id));
      } else {
        await addFavoriteNetwork(network.network_id);
        // Mettre à jour l'état local des favoris
        const newFavorite = { ...network, is_favorite: true };
        setFavoriteNetworks(prevFavorites => [...prevFavorites, newFavorite]);
      }
      
      // Invalider les caches concernés pour que les données soient rechargées à jour
      invalidateAllNetworksCache();
      
      fetchNetworks();

    } catch (error) {
      console.error('Erreur lors de la modification des favoris (NetworkList):', error);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      // Supprimer d'abord toutes les lignes en favoris
      for (const line of networkLines) {
        await removeFavoriteLine(selectedNetwork.network_id, line.line_id);
      }
      
      // Puis supprimer le réseau
      await removeFavoriteNetwork(selectedNetwork.network_id);
      
      // Mettre à jour les deux listes (favoris et réseaux)
      setFavoriteNetworks(prevFavorites => 
        prevFavorites.filter(n => n.network_id !== selectedNetwork.network_id)
      );

      // Mettre à jour l'état local
      setNetworks(prevNetworks => 
        prevNetworks.map(n => 
          n.network_id === selectedNetwork.network_id 
            ? { ...n, is_favorite: false } 
            : n
        )
      );
      
      setShowConfirmDialog(false);
      setSelectedNetwork(null);
      setNetworkLines([]);
    } catch (error) {
      console.error('Erreur lors de la suppression des favoris:', error);
    }
  };

  // Filtrer les réseaux pour exclure ceux qui sont en favoris
  const availableNetworks = networks.filter(
    network => !favoriteNetworks.some(fav => fav.network_id === network.network_id)
  );
  
  const filteredNetworks = availableNetworks.filter(network => 
    network.network_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFavorites = favoriteNetworks.filter(network => 
    network.network_name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Filtrer les réseaux par région en fonction de la recherche
  const filteredNetworksByRegion = {};
  const filteredRegions = [];
  
  regions.forEach(region => {
    if (networksByRegion[region]) {
      const filteredNetworksInRegion = networksByRegion[region].filter(
        network => 
          network.network_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !favoriteNetworks.some(fav => fav.network_id === network.network_id)
      );
      
      if (filteredNetworksInRegion.length > 0) {
        filteredNetworksByRegion[region] = filteredNetworksInRegion;
        filteredRegions.push(region);
      }
    }
  });
  
  // Liste des régions pour le sélecteur (avec "Tous" en premier)
  const allRegions = ['Tous', ...regions];
  
  // Régions filtrées selon la sélection
  const regionsToShow = selectedRegion === 'Tous' 
    ? filteredRegions 
    : filteredRegions.filter(region => region === selectedRegion);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
      <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg">
        <p className="text-red-700 dark:text-red-300">Erreur: {error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      <style>{simpleBorderStyle}</style>
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-md transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo.svg" 
                  alt="Bus Connect Logo" 
                  className="h-8 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/120x40?text=Bus+Connect';
                  }}
                />
                <span className="ml-2 px-2 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-[#07d6fb] to-[#ff66c4] text-white uppercase">
               v4 -   Bêta
                </span>
              </Link>
            </div>
      
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg overflow-hidden transition-colors duration-200">
            <div className="p-6">
              {/* Affichage conditionnel : Salutation ou Boutons Connexion/Inscription */}
              <div className="mb-4">
                {user ? (
                  <p className="text-sm">
                    <span className="text-gradient">
                      {getGreeting()} {user.user_metadata?.first_name || user.user_metadata?.name || user.email.split('@')[0]}, {currentBusMessage}
                    </span>
                  </p>
                ) : (
                  <>
                    <div className="text-center mb-4 text-gray-600 dark:text-gray-300">
                      Ajoutez vos réseaux, lignes de bus, vos arrêts en favoris en créant votre compte
                    </div>
                    <div className="flex space-x-3">
                      <Link 
                        to="/login" 
                        className="flex-1 w-full flex justify-center py-3 px-4 rounded-[30px] text-base font-medium text-white bg-[#22C55E] hover:bg-green-600 focus:outline-none transform transition-all duration-150"
                      >
                        Se connecter
                      </Link>
                      
                      <Link 
                        to="/register"
                        className="flex-1 w-full flex justify-center py-3 px-4 rounded-[30px] text-base font-medium text-white bg-[#2563EB] hover:bg-blue-600 focus:outline-none transform transition-all duration-150"
                      >
                        S'inscrire
                      </Link>
                    </div>
                  </>
                )}
              </div>
              
              <div className="search-container relative mb-8">
                <div className="search-icon">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un réseau..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input gradient-border dark:placeholder-gray-400"
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 transition-colors"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Sélecteur de régions */}
              <div className="region-filter-container">
                <label className="region-filter-label">
                  <IoFilter className="h-5 w-5 text-gray-400" />
                  Filtrer par région
                </label>
                <select 
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="region-select"
                >
                  {allRegions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
              
              {/* Section des réseaux favoris */}
              {user && (loading ? (
                <div className="mb-8">
                  <h2 className="page-title text-gradient">Vos réseaux favoris</h2>
                  <hr className="gradient-hr" />
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                    {[...Array(4)].map((_, index) => (
                      <SkeletonNetworkCard key={`favorite-skeleton-${index}`} />
                    ))}
                  </div>
                </div>
              ) : filteredFavorites.length > 0 && (
                <div className="mb-8">
                  <h2 className="page-title text-gradient">Vos réseaux favoris</h2>
                  <hr className="gradient-hr" />
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                    {filteredFavorites.map(network => (
                      <div
                        key={network.network_id}
                        onClick={() => handleNetworkClick(network.network_id)}
                        className="simple-gradient-border cursor-pointer relative"
                      >
                        {network.network_type && (
                          <span className="network-type-badge absolute top-2 right-2">{network.network_type}</span>
                        )}
                        <div className="card-content">
                          <div className="flex items-center justify-between mb-3">
                            <div className="network-logo-container">
                              <div className="network-logo-border"></div>
                              <div className="network-logo">
                                <NetworkLogo 
                                  networkId={network.network_id}
                                  networkName={network.network_name}
                                />
                              </div>
                            </div>
                            <FavoriteButton
                              type="network"
                              item={{
                                networkId: network.network_id,
                                networkName: network.network_name
                              }}
                              onClick={(e) => handleFavoriteClick(e, network, true)}
                              size="small"
                            />
                          </div>
                          <div className="mt-auto">
                            <h3 className="network-name truncate">{network.network_name}</h3>
                            <TextMarquee 
                              text={
                                network.agency_name ? 
                                  network.agency_name.includes(' - ') ?
                                    network.agency_name.split(' - ')[1] :
                                    network.agency_name
                                  : 'Inconnu'
                              }
                              className="agency-name"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Section des réseaux disponibles */}
              
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                  {[...Array(8)].map((_, index) => (
                    <SkeletonNetworkCard key={`network-skeleton-${index}`} />
                  ))}
                </div>
              ) : error ? (
                <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">Erreur: {error}</p>
                </div>
              ) : (
                <div>
                  {regionsToShow.map(region => (
                    <div key={region} className="mb-8">
                      <hr className="region-divider" />
                      <div className="region-header">
                        <div className="region-icon">
                          <RegionLogo region={region} />
                        </div>
                        <span className="region-title">{region}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                        {filteredNetworksByRegion[region].map(network => (
                          <div
                            key={network.network_id}
                            onClick={() => handleNetworkClick(network.network_id)}
                            className="simple-gradient-border cursor-pointer relative"
                          >
                            {network.network_type && (
                              <span className="network-type-badge absolute top-2 right-2">{network.network_type}</span>
                            )}
                            <div className="card-content">
                              <div className="flex items-center justify-between mb-3">
                                <div className="network-logo-container">
                                  <div className="network-logo-border"></div>
                                  <div className="network-logo">
                                    <NetworkLogo 
                                      networkId={network.network_id}
                                      networkName={network.network_name}
                                    />
                                  </div>
                                </div>
                                <FavoriteButton
                                  type="network"
                                  item={{
                                    networkId: network.network_id,
                                    networkName: network.network_name
                                  }}
                                  onClick={(e) => handleFavoriteClick(e, network)}
                                  size="small"
                                />
                              </div>
                              <div className="mt-auto">
                                <h3 className="network-name truncate">{network.network_name}</h3>
                                <TextMarquee 
                                  text={
                                    network.agency_name ? 
                                      network.agency_name.includes(' - ') ?
                                        network.agency_name.split(' - ')[1] :
                                        network.agency_name
                                      : 'Inconnu'
                                  }
                                  className="agency-name"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {filteredRegions.length === 0 && !loading && !error && !filteredFavorites.length && (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Aucun réseau trouvé</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Essayez avec un autre terme de recherche.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Dialog de confirmation */}
      {showConfirmDialog && selectedNetwork && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" onClick={() => setShowConfirmDialog(false)}></div>
          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-xl z-10 max-w-md w-full mx-4 transition-colors duration-200">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Attention</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Ce réseau a {networkLines.length} ligne{networkLines.length > 1 ? 's' : ''} en favoris.
              Souhaitez-vous quand même le retirer de vos favoris ?
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowConfirmDialog(false);
                  setSelectedNetwork(null);
                  setNetworkLines([]);
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={handleConfirmRemove}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Retirer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkList; 